import { createContext, useContext, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUsers } from "common";
import { API_STATUS } from "app/shared/constants";
import { LOADER_TYPES } from "./constants";
import {
  initLoading,
  initStudyRecruitmentData,
  initStudies,
  initCohorts,
  initStudyRecruitmentMetrics,
  initUserPerms,
} from "./initialStates";
import StudyRecruitmentApi from "./StudyRecruitmentApi";
import {
  generatePermissions,
  marhsalCohortToApi,
  marshalFormikToStudyRecruitmentApi,
} from "./serviceHelpers";

const StudyRecruitmentContext = createContext();

export const StudyRecruitmentProvider = ({ children }) => {
  const { protocolNumber } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useUsers();
  const [isLoading, setIsLoading] = useState(initLoading);
  const [userPerms, setUserPerms] = useState(initUserPerms);
  const [studyRecruitmentData, setStudyRecruitmentData] = useState(
    initStudyRecruitmentData,
  );
  const [studyRecruitmentMetrics, setStudyRecruitmentMetrics] = useState(
    initStudyRecruitmentMetrics,
  );
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [studies, setStudies] = useState(initStudies);
  const [resumeActivationDate, setResumeActivationDate] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [cohorts, setCohorts] = useState(initCohorts);

  const api = StudyRecruitmentApi();

  // Pseudo-reducer to keep loading states updated
  const setLoader = (type, isSet) => {
    setIsLoading((prev) => ({
      ...prev,
      [type]: isSet,
    }));
  };

  /**********************************************
   * Async functions for use inside useEffects. *
   **********************************************/

  /**
   * Get All Studies
   */
  const getStudies = async () => {
    setLoader(LOADER_TYPES.STUDIES, true);

    const studies = await api.getStudies(currentUser.uid);

    if (studies === API_STATUS.API_ERROR) {
      setSelectedStudy(null);
      setStudies({ ...initStudies });

      setLoader(LOADER_TYPES.STUDIES, false);
      return;
    }

    const currentPerms = generatePermissions(currentUser);

    if (!protocolNumber && studies.length === 0) {
      navigate(`/study-recruitment/noStudyAssociationFound`);
    } else if (
      protocolNumber &&
      !studies.some((study) => study.protocolNumber === protocolNumber) &&
      !currentPerms.canViewStudyRecruitment
    ) {
      navigate(`/study-recruitment/${protocolNumber}/noStudyAssociationFound`);
    }

    if (!protocolNumber && studies.length > 0) {
      navigate(`/study-recruitment/${studies[0].protocolNumber}`);
    }

    setSelectedStudy(
      studies.find((study) => study.protocolNumber === protocolNumber),
    );
    setStudies(studies);

    setLoader(LOADER_TYPES.STUDIES, false);
  };

  /**
   * Get Study Recruitment Data
   */
  const getStudyRecruitmentData = async () => {
    setLoader(LOADER_TYPES.STUDY_RECRUITMENT_DATA, true);

    const studyRecruitmentData =
      await api.getStudyRecruitmentData(protocolNumber);

    if (studyRecruitmentData === API_STATUS.API_ERROR) {
      setStudyRecruitmentData({ ...initStudyRecruitmentData });

      setLoader(LOADER_TYPES.STUDY_RECRUITMENT_DATA, false);
      return;
    }

    setStudyRecruitmentData(studyRecruitmentData);

    setLoader(LOADER_TYPES.STUDY_RECRUITMENT_DATA, false);
  };

  /**
   * Get Study Recruitment Metrics
   */
  const getStudyRecruitmentMetrics = async () => {
    setLoader(LOADER_TYPES.STUDY_RECRUITMENT_METRICS, true);

    const studyRecruitmentMetrics =
      await api.getStudyRecruitmentMetrics(protocolNumber);

    if (studyRecruitmentMetrics === API_STATUS.API_ERROR) {
      setStudyRecruitmentMetrics({ ...initStudyRecruitmentMetrics });

      setLoader(LOADER_TYPES.STUDY_RECRUITMENT_METRICS, false);
      return;
    }

    setStudyRecruitmentMetrics(studyRecruitmentMetrics);

    setLoader(LOADER_TYPES.STUDY_RECRUITMENT_METRICS, false);
  };

  /**
   * Get Study Team Outreach Cohorts
   */
  const getOutreachCohorts = async () => {
    setLoader(LOADER_TYPES.OUTREACH_COHORTS, true);

    const outreachCohorts = await api.getOutreachCohorts(protocolNumber);

    if (outreachCohorts === API_STATUS.API_ERROR) {
      setResumeActivationDate(null);
      setSelectedCohort(null);
      setCohorts({ ...initCohorts });

      setLoader(LOADER_TYPES.OUTREACH_COHORTS, false);
      return;
    }

    const defaultCohort = outreachCohorts.find(
      (cohort) => cohort.isDefault === true,
    );
    const sortedOutreachCohorts = outreachCohorts.sort((a, b) =>
      a.filename.localeCompare(b.filename),
    );

    setResumeActivationDate(defaultCohort?.studyTeamOutreachResumeDate);
    setSelectedCohort(defaultCohort);
    setCohorts(sortedOutreachCohorts);

    setLoader(LOADER_TYPES.OUTREACH_COHORTS, false);
  };

  /**
   * General helper to save a study recruitment to the API.
   *
   * @param {string} studyId The ID of the study
   * @param {object} formData The formik state of the form
   */
  const saveStudyRecruitmentForm = async (studyId, formData) => {
    setLoader(LOADER_TYPES.SAVE_STUDY_RECRUITMENT_DATA, true);

    const apiData = marshalFormikToStudyRecruitmentApi(formData);

    const response = await api.saveStudyRecruitment(studyId, apiData);

    if (response.status === API_STATUS.API_SUCCESS) {
      setStudyRecruitmentData(response.data);
    }

    setLoader(LOADER_TYPES.SAVE_STUDY_RECRUITMENT_DATA, false);
  };

  /**
   * General helper to update Outreach Cohort Status to the API.
   *
   * @param {string} cohortId The ID of the cohort
   * @param {string} studyTeamOutreachStatus The status to update the cohort to
   * @param {string} studyTeamOutreachResumeDate The date to resume activation for the cohort
   */
  const updateOutreachCohortStatus = async (
    cohortId,
    studyTeamOutreachStatus,
    studyTeamOutreachResumeDate,
  ) => {
    setLoader(LOADER_TYPES.UPDATE_OUTREACH_COHORT_STATUS, true);

    const apiData = marhsalCohortToApi(
      studyTeamOutreachStatus,
      studyTeamOutreachResumeDate,
    );

    const response = await api.updateOutreachCohortStatus(cohortId, apiData);

    if (response.status === API_STATUS.API_SUCCESS) {
      setResumeActivationDate(response.data?.studyTeamOutreachResumeDate);
      setSelectedCohort(response.data);
      setCohorts((prevCohorts) =>
        prevCohorts.map((cohort) =>
          cohort.id === cohortId ? response.data : cohort,
        ),
      );
    }

    setLoader(LOADER_TYPES.UPDATE_OUTREACH_COHORT_STATUS, false);
  };

  /********************
   *    useEffects    *
   ********************/

  // Determine what permissions the current user has on
  // the page and set the state flags appropriately
  useEffect(() => {
    const userPermFlags = generatePermissions(currentUser);

    setUserPerms(userPermFlags);
  }, [currentUser]);

  // Initialize app
  useEffect(() => {
    getStudies();

    if (protocolNumber) {
      getStudyRecruitmentData();
      getStudyRecruitmentMetrics();
      getOutreachCohorts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocolNumber]);

  const providedValues = {
    isLoading,
    userPerms,
    protocolNumber,
    studyRecruitmentData,
    studyRecruitmentMetrics,
    selectedStudy,
    setSelectedStudy,
    studies,
    resumeActivationDate,
    setResumeActivationDate,
    selectedCohort,
    setSelectedCohort,
    cohorts,
    getStudyRecruitmentData,
    saveStudyRecruitmentForm,
    getStudies,
    updateOutreachCohortStatus,
  };

  return (
    <StudyRecruitmentContext.Provider value={providedValues}>
      {children}
    </StudyRecruitmentContext.Provider>
  );
};

export const useStudyRecruitment = () => {
  const context = useContext(StudyRecruitmentContext);
  if (!context) {
    throw new Error(
      "useStudyRecruitment must be used within an StudyRecruitmentProvider. " +
        "Wrap a parent component in <StudyRecruitmentProvider> to fix this error.",
    );
  }

  return context;
};
